/**
 * The hub's top bar.
 *
 * Its own component because the element ran forty lines inline — a member
 * block, a points block, a nav callback and a hand-rolled invite button with
 * its own SVG — inside a `chrome={...}` prop, in a file already past the
 * 400-line ceiling its own header cites.
 *
 * Presentational: every decision arrives as a prop.
 */
import React from "react";
import {SurfaceTopBar} from "@/surfaces/spine/SurfaceTopBar";
import {topBarProps, type SpineDrop} from "@/surfaces/spine/dropSpineData";
import {SURFACE_LEGAL_LINKS, SURFACE_ACCOUNT_ACTIONS, BRAND_ACCOUNT_ACTIONS} from "@/surfaces/spine/legalLinks";
import {ChallengeProfileEdit} from "@/components/challenge/ChallengeProfileEdit";
import EditProfilePictureModal from "@/components/modals/EditProfilePictureModal";

export interface DropHubTopBarProps {
    drop: SpineDrop;
    memberName?: string;
    user?: {
        profilePicture?: { cdnUrl?: string };
    };
    pointDisplay: { pointsName: string; currentPoints: number; currentLevel: number };
    /*
     * The account menu is built HERE rather than passed in: its three entries
     * are fixed (`SURFACE_ACCOUNT_ACTIONS`) and only their handlers vary, so
     * the caller supplies the two handlers and not the menu.
     */
    onSignOut: () => void;
    onHelp: () => void;
    activeSection: string | null;
    setActiveSection: (id: string | null) => void;
    onInvite: () => void;
    /** Signed-out sign-in trigger (opens the platform login modal). */
    onSignIn?: () => void;
    /**
     * The member's standing ON THIS SURFACE (board rank · points · period
     * word), resolved by the surface from its own board. Absent renders
     * NOTHING — platform identity (level, XP) never substitutes here.
     */
    memberStanding?: string;
    /** Badges earned, shown as a count on the member's name. Zero renders nothing. */
    badgeCount?: number;
    /** Pre-formatted week line for the campaign bar. Absent = no board = suppressed. */
    campaignWeekLabel?: string;
}

export const DropHubTopBar: React.FC<DropHubTopBarProps> = ({
    drop, memberName, user, pointDisplay, onSignOut, onHelp, activeSection, setActiveSection, onInvite,
    memberStanding, badgeCount, campaignWeekLabel, onSignIn,
}) => {
    /*
     * NO BELL ON A CAMPAIGN BAR (Shelly, 2026-09-10).
     *
     * It was mirrored here from the platform nav (`DevourTopNav`), where
     * notifications, friend requests and DMs are real. A brand campaign has
     * none of that, so on Road to the Finals it rang for nothing — furniture
     * in the one corner a member looks to for their own standing. The account
     * menu beside the name already carries the surface's personal actions.
     */

    /* Brand campaigns carry the brand-challenge menu set, exactly. */
    const actionSet = drop?.pov === "BRAND" ? BRAND_ACCOUNT_ACTIONS : SURFACE_ACCOUNT_ACTIONS;
    /*
     * My profile on a brand surface opens the in-context editor — RTTR's
     * grammar ("update avatar + nickname without leaving the experience"),
     * built into ChallengeProfileEdit for exactly this reuse. Navigating to
     * /account left the campaign, which is the thing RTTR removed in May.
     */
    const [isProfileOpen, setIsProfileOpen] = React.useState(false);
    const accountItems = React.useMemo(
        () => actionSet.map(a => ({
            ...a,
            onClick: a.id === "signout"
                ? onSignOut
                : a.id === "help"
                    ? onHelp
                    : a.id === "invite"
                        ? onInvite
                        /*
                         * Profile opens IN CONTEXT on every surface — the platform's own
                         * account menu does the same (EditProfilePictureModal), and RTTR
                         * removed its /profile navigation in May for exactly this reason.
                         * The brand POV gets the brand-tokened editor.
                         */
                        : a.id === "edit-profile" ? () => setIsProfileOpen(true) : undefined,
        })),
        [actionSet, drop?.pov, onSignOut, onHelp, onInvite],
    );

    return <>
        <SurfaceTopBar
            {...topBarProps(drop as SpineDrop, {campaignWeekLabel})}
            memberName={memberName}
            /*
             * A SIGNED-IN MEMBER IS ONE WE HAVE A USER FOR — never one we have
             * a NAME for.
             *
             * This required `memberName`, which is `nickname || firstName
             * lastName || undefined`. A member who never set either — every
             * Race to the Realm veteran who signed up with an email and
             * nothing else — rendered no member block, so the bar fell through
             * to its signed-out branch and showed SIGN IN to somebody who was
             * fully authenticated. Worse, it was self-sealing: the account
             * menu is the only place to set a name, and the account menu was
             * the thing being hidden.
             *
             * The name may be blank. The member is still a member.
             */
            member={user
                ? {
                    /*
                     * NO GRAVATAR ON A BRANDED SURFACE.
                     *
                     * The chain used to fall through to `user.gravatar`, which
                     * for an email with no Gravatar account serves a generated
                     * identicon — a green geometric tile landing inside a
                     * Minelab-red ring on Road to the Finals. `skipGravatar`
                     * exists for exactly this case and names brand surfaces in
                     * its own contract; the bar simply never opted in. With no
                     * upload the monogram renders instead, in the Drop's colour.
                     */
                    imageUrl: user.profilePicture?.cdnUrl,
                    /*
                     * Always a glyph. With gravatar gone, an unnamed member —
                     * signed up with an email and nothing else — had neither
                     * image nor monogram and rendered an empty disc inside the
                     * brand ring. "?" is what the RTTR header falls back to.
                     */
                    initials: memberName?.trim().slice(0, 2)
                        .toUpperCase() || "?",
                    name: memberName ?? "",
                    /*
                     * Surface standing or NOTHING. `Level N` was the
                     * platform's number wearing the surface's clothes —
                     * the exact substitution the member prop's contract
                     * forbids.
                     */
                    standing: memberStanding,
                    badgeCount,
                    /*
                     * A FIGURE, NOT A METER. `pointDisplay`
                     * carries a hardcoded `nextLevelPoints:
                     * 1000` against lifetime XP, so the pair
                     * reads "450,099 / 1,000" and a bar pins
                     * permanently full — telling the viewer they
                     * have finished something they have not.
                     * `useGetLevelConfigs()` is the real source
                     * for a level's threshold; until the page
                     * tree moves to it, this states what is
                     * true.
                     */
                    progressLabel: `${pointDisplay.currentPoints.toLocaleString()} ${pointDisplay.pointsName}`,
                    progress: undefined,
                }
                : undefined}
            onSignIn={onSignIn}
            accountItems={accountItems}
            /*
             * Brand menu matches the brand-challenge menus exactly — no legal
             * footer in it (Shelly, 2026-09-01). Legal stays on the action bar.
             */
            legal={drop?.pov === "BRAND" ? [] : SURFACE_LEGAL_LINKS}
            platformLogoUrl={drop?.pov === "BRAND" ? undefined : "/images/DevourPlayLogoWhite.png"}
            /*
             * No `actions`: the bar's only personal control is the account
             * menu beside the member's name. See the note at the top.
             */
        />
        {/* Conditional mount, like RTTR's — the overlay exists only while open. */}
        {isProfileOpen && drop?.pov === "BRAND" &&
            <ChallengeProfileEdit
                isOpen={isProfileOpen}
                onClose={() => setIsProfileOpen(false)}
                brand={{
                    backdropBg: "rgba(0, 0, 0, 0.75)",
                    accentColor: drop?.themeColors?.primary,
                    logoUrl: drop?.logoImageUrl,
                    logoAlt: drop?.name,
                }}
                title="Edit Profile"
                helpText="Use the name you'd like to appear on the leaderboard."
            />}
        {isProfileOpen && drop?.pov !== "BRAND" &&
            <EditProfilePictureModal
                isOpen={isProfileOpen}
                onClose={() => setIsProfileOpen(false)}
                onProfilePictureUpdate={() => {}}
            />}
    </>;
};
